import { useState, useRef, useEffect, useCallback } from 'react';

export const useSpeechToText = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState(null);

  const audioStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const workletNodeRef = useRef(null);
  const onStopRef = useRef(null);
  const socketRef = useRef(null);

  const resolveMode = (options = {}) => options?.mode || 'legacy';

  const stopRecording = useCallback((socket, onStop, options = {}) => {
    // Use the passed-in onStop if available, otherwise use the one from the ref
    const stopCallback = typeof onStop === 'function' ? onStop : onStopRef.current;

    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    
    // Use functional update to avoid dependency on isRecording in useCallback
    setIsRecording(currentIsRecording => {
      if (currentIsRecording) {
        setIsSpeaking(false);

        const socketToUse = socket || socketRef.current;
        if (socketToUse && socketToUse.readyState === WebSocket.OPEN) {
          const mode = resolveMode(options);
          if (mode === 'b2b_v2') {
            socketToUse.send(
              JSON.stringify({
                type: 'stop_audio',
                session_id: options?.sessionId || null,
                sample_rate: 16000,
                content_type: 'audio/pcm',
              })
            );
          } else {
            socketToUse.send(JSON.stringify({ event: 'stop_speech' }));
          }
        }
        
        if (stopCallback) {
          setTimeout(stopCallback, 100);
        }
      }
      return false; // Always return false to ensure recording is stopped
    });
  }, []);

  const startRecording = async (socket, userId, chatId, userName, applicationId, onStop, options = {}) => {
    setError(null);
    if (isRecording) {
      return;
    }

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setError("WebSocket connection is not available.");
      return;
    }
    
    onStopRef.current = onStop;
    socketRef.current = socket;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      setIsRecording(true);

      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 16000
      });
      
      await audioContextRef.current.audioWorklet.addModule('/audio-processor.js');
      workletNodeRef.current = new AudioWorkletNode(audioContextRef.current, 'audio-processor');
      
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(workletNodeRef.current);
      workletNodeRef.current.connect(audioContextRef.current.destination);

      workletNodeRef.current.port.onmessage = (event) => {
        if (event.data && event.data.type === 'silence') {
          // Silence detected, auto-stop recording
          stopRecording(socket, onStop, options);
          return;
        }
        const pcmData = event.data;
        if (socket && socket.readyState === WebSocket.OPEN && pcmData instanceof Int16Array) {
          const base64String = btoa(
            new Uint8Array(pcmData.buffer)
              .reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          const mode = resolveMode(options);
          const message =
            mode === 'b2b_v2'
              ? {
                  type: 'audio_chunk',
                  session_id: options?.sessionId || chatId || null,
                  audio_base64: base64String,
                  sample_rate: 16000,
                  content_type: 'audio/pcm',
                }
              : {
                  audio_data: base64String,
                  userId: userId,
                  chatId: chatId,
                  user_name: userName,
                  application_id: applicationId,
                  sample_rate: 16000,
                  content_type: 'audio/pcm',
                };
          socket.send(JSON.stringify(message));
        }
      };

    } catch (err) {
      console.error('Error accessing microphone or setting up audio worklet:', err);
      setError('Microphone access denied or audio setup failed.');
      setIsRecording(false);
    }
  };

  useEffect(() => {
    return () => {
      if (isRecording) {
        stopRecording();
      }
    };
  }, [isRecording, stopRecording]);

  return {
    isRecording,
    isSpeaking,
    error,
    startRecording,
    stopRecording,
    setIsRecording, // Expose setter for external control
    setIsSpeaking,
  };
};
